import React from 'react';
import useDerivedMetricByWeekId from 'src/serverProxy/useDerivedMetricByWeekId';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName
} from 'src/utils/Hooks';
import TrafficConversionRateGrid from './TrafficConversionRateGrid';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { useMainEntityType, useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { TrafficConversionByWeekTable } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Conversion/TrafficConversionByWeekTable';
import { TrafficNoDataPlaceholder } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/pages/NonTopicOrTopicGroupPage';
import { useTrafficAvailability } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/Total/hooks/useTrafficAvailability';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { shouldShowBeaconProTopics } from 'src/utils/app';
import convertLineChartSeriesToDelimitedData, {
  Series
} from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import saveAs from 'file-saver';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

/**
 * Conversion Page for the traffic side tab
 */
export default function TrafficConversionPage() {
  const primaryLegendDisplayName = useAppSelector((state) => state.mainTimePeriod.displayName);
  const comparisonLegendDisplayName = useAppSelector((state) => state.comparisonTimePeriod.displayName);
  const {
    primaryData,
    secondaryData,
    percentChange,
    isLoading,
    formattedPercentChange,
    formattedPrimaryTotal,
    formattedSecondaryTotal
  } = useDerivedMetricByWeekId('sales', 'purchaseRate');
  const { isProductEntity } = useProductEntity();

  const { dataAvailable } = useTrafficAvailability();
  const mainPeriodDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonDisplayName = useComparisonTimePeriodShortDisplayName();
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const mainEntityType = useMainEntityType();
  const isCategoryEntity = mainEntityType === 'category';
  const isSubcategoryEntity = mainEntityType === 'subcategory';
  const titleOnly = !(isProductEntity || isCategoryEntity || isSubcategoryEntity);
  const entityPhrase = useEntityPhrasing({ titleOnly });
  const taxonomyText = useTaxonomySubtitleText();

  const titleToUse = 'Conversion Rate';

  const getSubtitle = () => {
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the conversion rate for ${entityPhrase}`}
          {taxonomyText}
          {` during the ${mainPeriodDisplayName} period compared to the ${comparisonDisplayName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  const convertSeriesToDelimitedData = () => {
    try {
      // Convert the chart series to delimited data (may return a promise)
      const data = convertLineChartSeriesToDelimitedData(
        [
          {
            type: 'spline',
            name: mainTimePeriod.displayName,
            data: primaryData.map((row) => ({
              x: row[0],
              y: row[1],
              marker: {
                enabled: true
              }
            }))
          } as unknown as Series,
          {
            type: 'spline',
            name: comparisonTimePeriod.displayName,
            data: secondaryData.map((row) => ({
              x: row[0],
              y: row[1],
              marker: {
                enabled: true
              }
            }))
          } as unknown as Series
        ],
        comparisonTimePeriod.id === 'prior-period',
        comparisonTimePeriod,
        mainTimePeriod
      );

      // Create a blob with the data and save it as a CSV file
      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `${titleToUse}.csv`);
    } catch (e) {
      console.error('error: ', e);
    }

    return Promise.resolve();
  };

  return (
    <BasePageContainer>
      {dataAvailable ? (
        <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
          <BeaconChartWithLegend
            convertSeriesToDelimitedData={convertSeriesToDelimitedData}
            title="Conversion Rate"
            subtitle={shouldShowBeaconProTopics() ? getSubtitle() : undefined}
            includeSummaryInfo={!shouldShowBeaconProTopics()}
            loading={isLoading}
            loadingComponent={<SplineChartLoading />}
            primaryLegendProps={{
              displayName: primaryLegendDisplayName,
              metric: formattedPrimaryTotal,
              change: formattedPercentChange,
              direction: getDirection(percentChange)
            }}
            comparisonLegendProps={{
              displayName: comparisonLegendDisplayName,
              metric: formattedSecondaryTotal
            }}
          >
            <SplineChart metricType={METRICTYPE.PERCENT} primaryData={primaryData} secondaryData={secondaryData} />
          </BeaconChartWithLegend>
          {isProductEntity ? <TrafficConversionByWeekTable /> : <TrafficConversionRateGrid />}
        </Flex>
      ) : (
        <TrafficNoDataPlaceholder />
      )}
    </BasePageContainer>
  );
}
